import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { InputAdornment, OutlinedInput } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import config from '../../config';
import { PRODUCT } from '../../config/fe-routes';
import { useAttributionOrigin } from '../../hooks/useAttributionOrigin';
import { usePriceFormatter } from '../../hooks/usePriceFormatter';
import useUser from '../../hooks/useUser';
import OfferService from '../../services/offer.service';
import OfferLoginService from '../../services/offer-login.service';
import { openLoginModal } from '../../store/actions/login.actions';
import { OfferType } from '../../types/offer.types';
import { ProductType } from '../../types/product.types';
import { getCurrencySymbol, stringToPrice } from '../../utils/price-format';
import { useNotification } from '../../utils/snackbar';
import { OfferInfoModal } from '../OfferInfoModal/OfferInfoModal';
import Button from '../ui-kit/Button/Button';
import Modal from '../ui-kit/Modal/Modal';

const offerService = new OfferService();
const offerLoginService = new OfferLoginService();

export interface ProductMakeOfferModalProps {
  product: ProductType;
  open: boolean;
  onClose: () => void;
  /** Pass the existing offer when making a counter offer */
  offer?: OfferType;
  /** Whether the current user is the product seller — only relevant for counter offers */
  isSeller?: boolean;
  /** Pre-fill the offer price, e.g. when restoring a guest offer after login */
  initialOfferPrice?: string;
  /** Called after an offer is successfully created, so the parent can refresh product/offer state */
  onOfferCreated?: () => void;
}

const ProductMakeOfferModal = ({
  product,
  open,
  onClose,
  offer,
  isSeller,
  initialOfferPrice,
  onOfferCreated,
}: ProductMakeOfferModalProps) => {
  const { t } = useTranslation();
  const { query } = useRouter();
  const { user } = useUser();
  const formatPrice = usePriceFormatter();
  const { showErrorNotification } = useNotification();
  const attributionOrigin = useAttributionOrigin(product.slug);

  const shopSlug = query['shop-slug']?.toString();
  const productSlug = query['product-slug']?.toString();

  const [offerPrice, setOfferPrice] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);

  useEffect(() => {
    if (open && initialOfferPrice) {
      setOfferPrice(initialOfferPrice);
    }
  }, [open, initialOfferPrice]);

  const maxDiffPercentage = config.offer.maxDiffPercentage;
  const productPrice = +product.price;
  const baseLowestAllowedPrice = Math.round((productPrice - (productPrice / 100) * maxDiffPercentage) * 100) / 100;

  const isCounterOffer = !!offer?.id;
  const isCounterOfferBySeller = isCounterOffer && !!isSeller;
  const isCounterOfferByBuyer = isCounterOffer && !isSeller;

  // For seller counter offer, minimum price is the higher of base minimum and buyer's offer
  const lowestAllowedOfferPrice = isCounterOfferBySeller ? Math.max(baseLowestAllowedPrice, offer.offeredPrice) : baseLowestAllowedPrice;

  // For buyer counter offer, maximum is their previous offer price (must undercut it)
  const maxAllowedPrice = isCounterOfferByBuyer ? offer.offeredPrice : productPrice;

  const handleClose = () => {
    setOfferPrice('');
    setError('');
    onClose();
  };

  const validate = (price: number): string | null => {
    if (isCounterOfferBySeller) {
      if (price <= lowestAllowedOfferPrice) {
        return t('pages.offer.seller-counter-diff-max-err', {
          formattedPrice: formatPrice(lowestAllowedOfferPrice),
          formattedOldPrice: formatPrice(offer.offeredPrice),
        });
      }
      if (price >= productPrice) {
        return t('pages.offer.diff-min-err');
      }
    } else if (isCounterOfferByBuyer) {
      if (price < baseLowestAllowedPrice) {
        return t('pages.offer.diff-max-err', { formattedPrice: formatPrice(baseLowestAllowedPrice), percentage: maxDiffPercentage });
      }
      if (price >= offer.offeredPrice) {
        return t('pages.offer.buyer-counter-diff-min-err', { formattedOldPrice: formatPrice(offer.offeredPrice) });
      }
    } else {
      if (price < lowestAllowedOfferPrice) {
        return t('pages.offer.diff-max-err', { formattedPrice: formatPrice(lowestAllowedOfferPrice), percentage: maxDiffPercentage });
      }
      if (price >= productPrice) {
        return t('pages.offer.diff-min-err');
      }
    }

    return null;
  };

  // Guests can't make offers — save whatever they typed and open the login modal, then restore it after login
  const handleGuestLogin = () => {
    offerLoginService.setOfferData({ productId: product.id, offeredPrice: offerPrice });
    openLoginModal(true, PRODUCT(shopSlug, productSlug));
    handleClose();
  };

  const handleSubmit = async () => {
    const price = +offerPrice;
    const validationError = validate(price);

    if (validationError) {
      setError(validationError);

      return;
    }

    try {
      setIsSaving(true);

      if (isCounterOffer) {
        await offerService.createCounterOffer({ offerId: offer.id, offeredPrice: price }, { silentFail: true });
      } else {
        await offerService.createOffer({ productId: product.id, offeredPrice: price, ...attributionOrigin }, { silentFail: true });
      }

      setIsSaving(false);
      onOfferCreated?.();
      handleClose();
    } catch (err) {
      setIsSaving(false);
      showErrorNotification({ error: err });
    }
  };

  const parsedOfferPrice = +offerPrice;
  const effectiveLowestPrice = isCounterOfferByBuyer ? baseLowestAllowedPrice : lowestAllowedOfferPrice;
  const isPriceTooLow = !!offerPrice?.trim() && parsedOfferPrice < effectiveLowestPrice;
  const isSubmitDisabled =
    isSaving || !offerPrice?.trim() || parsedOfferPrice >= maxAllowedPrice || parsedOfferPrice < effectiveLowestPrice;

  const title = isCounterOffer ? t('common.make-counter-offer') : t('common.make-offer');

  return (
    <>
      <Modal open={open} onClose={handleClose} showCloseButton className="product-make-offer-modal" title={title}>
        <div className="product-make-offer-modal__product">
          {product.images?.[0] && (
            <img className="product-make-offer-modal__product-image" src={product.images[0].thumbnail} alt={product.name} />
          )}
          <div className="product-make-offer-modal__product-details">
            <p className="product-make-offer-modal__product-name">
              {product.description?.length > 100 ? `${product.description.slice(0, 100)}…` : product.description}
            </p>
            {(product?.brand || product?.size) && (
              <p className="product-make-offer-modal__product-meta">
                <span>{[product.brand, product.size].filter(Boolean).join(', ')}</span>
              </p>
            )}
            <p className="product-make-offer-modal__product-price">{formatPrice(product.price)}</p>
          </div>
        </div>

        <div className="product-make-offer-modal__content">
          <div className="product-make-offer-modal__input-group">
            <label className="product-make-offer-modal__label">
              {isCounterOffer ? t('pages.offer.your-counter-offer') : t('pages.offer.your-offer')}
            </label>
            <OutlinedInput
              fullWidth
              className="product-make-offer-modal__price-input"
              startAdornment={
                <InputAdornment position="start">
                  <span className="product-make-offer-modal__currency">{getCurrencySymbol()}</span>
                </InputAdornment>
              }
              value={offerPrice}
              onChange={(e) => {
                setOfferPrice(stringToPrice(e.target.value));
                setError('');
              }}
              error={!!error || isPriceTooLow}
            />
            {error ? (
              <p className="product-make-offer-modal__caption product-make-offer-modal__caption--error">{error}</p>
            ) : !isCounterOfferBySeller ? (
              <p className={`product-make-offer-modal__caption${isPriceTooLow ? ' product-make-offer-modal__caption--error' : ''}`}>
                {t('pages.offer.diff-max-err', { formattedPrice: formatPrice(baseLowestAllowedPrice), percentage: maxDiffPercentage })}
              </p>
            ) : null}
          </div>

          <Button
            buttonType="primary"
            fullWidth
            disabled={user?.isLoggedIn ? isSubmitDisabled : false}
            onClick={user?.isLoggedIn ? handleSubmit : handleGuestLogin}
            className="product-make-offer-modal__send-btn"
            size="large"
          >
            {!user?.isLoggedIn ? t('pages.offer.login') : t('pages.offer.send')}
          </Button>

          <button className="product-make-offer-modal__info-link" onClick={() => setInfoModalOpen(true)}>
            {t('pages.offer.learn-more')}
            <InfoOutlinedIcon className="product-make-offer-modal__info-icon" />
          </button>
        </div>
      </Modal>

      <OfferInfoModal isOffersModalOpen={isInfoModalOpen} setOffersModalOpen={setInfoModalOpen} hideBackdrop />
    </>
  );
};

export default ProductMakeOfferModal;
